import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { relationOf } from './mechanics/relations';
import './SceneArranger.css';

/**
 * The creator places every item in the scene themselves (backend game.save_layout). Click an item
 * to pick it up, click anywhere on the background to put it there (its feet land where you click),
 * or drag it. The size slider sets its width. Saved positions are used by every later build/play.
 * Positions are % of the scene: x/y = the item's bottom centre, width = % of the scene width.
 *
 * Problem pairs (spec.pairs, backend game.problem_pairs) only show the problem when seen together -
 * boxes in a camera's view, a pallet propping a door. Only LOCKED pairs are kept together: moving the
 * host carries its parts, and a part dropped away snaps back (same rule as backend game.enforce_pairs).
 * A placement ("the cup starts on the counter by the bottle") is just where it starts - move it freely.
 *
 * Pictures: each item's picture was matched by the art check (spec.art); pick another for the selected
 * item from the sheet's pictures (spec.pictures) - any picture, even one another item uses (two cups can
 * share the cup) - your choice wins on every later build. Or Redraw it: one image call for that item alone,
 * in your own words ("a clear plastic measuring cup with ml marks").
 * The checklist (spec.checklist, backend game.checklist) sits on top: what to put right before publishing.
 *
 * Library (inventory missions): the background comes from the shared library of rooms - "Background…" shows the room's
 * other backgrounds (free to pick) before "Generate a new one" (one image, added to the library for everyone). A
 * selected inventory item shows its pictures there too (its own, and any a creator had drawn).
 */
const API_BASE = process.env.REACT_APP_API_URL || '';
const asset = (src) => (src && src.startsWith('/game-builder-assets') ? `${API_BASE}${src}` : src);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const LEVEL_ICON = { ok: '✅', warn: '⚠️', error: '⛔' };

const ART_NOTE = {
  creator: 'your pick', redrawn: 'redrawn for it', checked: 'matched by the art check',
  shared: "borrowed from its look-alike", order: 'not checked - have a look', missing: 'no picture shows it yet'
};

// `initialSelected`: the item (its name) to open on - one clicked in the mission card's preview
export default function SceneArranger({ spec, onSave, onCancel, onRedoBackground = null, onRedraw = null, library = null, initialSelected = null }) {
  const stageRef = useRef(null);
  const itemRefs = useRef({});
  const dragRef = useRef(null);
  const [positions, setPositions] = useState(null);   // { item: {x, y, width} }
  const [selected, setSelected] = useState(initialSelected);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // The creator's picture choices: {item id: picture number}; starts from the ones they made before.
  const [art, setArt] = useState(() => Object.fromEntries(Object.entries(spec.art || {}).filter(([, a]) => a.by === 'creator').map(([id, a]) => [id, a.picture])));
  const pictureOf = (o) => (art[o.id] !== undefined ? spec.pictures?.[art[o.id]]?.src : o.src);
  const [redraw, setRedraw] = useState(null);         // { id, looks, busy, error } - the redraw form for one item
  const [rooms, setRooms] = useState(null);           // the room's backgrounds in the library, while the picker is open
  const [itemPics, setItemPics] = useState(null);     // { id, current, variants } for the selected inventory item
  const [toDraw, setToDraw] = useState([]);           // item ids ticked for "Draw pictures"
  const [drawing, setDrawing] = useState(false);
  const [drawError, setDrawError] = useState('');      // shown by the button (the top of the window may be out of view)
  const drawPictures = async () => {
    setDrawing(true);
    setDrawError('');
    setError('');
    try { await library.drawMany(toDraw); setToDraw([]); } catch (e) { setDrawError(e.message); } finally { setDrawing(false); }
  };
  const drawable = spec.items.filter((o) => o.inv && o.kind !== 'place');
  // a drawn sheet's picture on the wrong item: this one takes the other's picture and gives its own
  const [swapping, setSwapping] = useState(false);
  const swapWith = async (o, other) => {
    if (!other) return;
    setSwapping(true);
    setDrawError('');
    try {
      await library.swapPicture(o.id, other);
      setArt((a) => { const next = { ...a }; delete next[o.id]; delete next[other]; return next; });   // show the swapped pictures
    } catch (e) { setDrawError(e.message); } finally { setSwapping(false); }
  };
  const openBackgrounds = async () => {
    try { setRooms(await library.backgrounds()); } catch (e) { setError(e.message); }
  };
  const pickBackground = async (variant) => {
    try { await library.chooseBackground(variant); setRooms((r) => ({ ...r, current: variant })); } catch (e) { setError(e.message); }
  };
  const startRedraw = (o) => setRedraw({ id: o.id, looks: o.looks || '', busy: false, error: '' });
  const doRedraw = async () => {
    setRedraw((r) => ({ ...r, busy: true, error: '' }));
    try {
      await onRedraw(redraw.id, redraw.looks.trim());
      setArt((a) => { const next = { ...a }; delete next[redraw.id]; return next; });   // show the new drawing
      setRedraw(null);
    } catch (e) {
      setRedraw((r) => ({ ...r, busy: false, error: e.message }));
    }
  };

  const selectedItem = spec.items.find((o) => o.item === selected);
  useEffect(() => {
    let live = true;
    setItemPics(null);
    if (library && selectedItem?.inv) {
      library.pictures(selectedItem.id).then((p) => { if (live) setItemPics({ id: selectedItem.id, ...p }); }).catch(() => {});
    }
    return () => { live = false; };
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const pickPicture = async (ref) => {
    try { await library.choosePicture(selectedItem.id, ref); setItemPics((p) => ({ ...p, current: ref })); } catch (e) { setError(e.message); }
  };
  // an object drawn per state (frayed / replaced): a picture is chosen for each state
  const pickStatePicture = async (state, ref) => {
    try {
      await library.choosePicture(selectedItem.id, ref, state);
      setItemPics((p) => ({ ...p, states: p.states.map((x) => (x.state === state ? { ...x, current: ref } : x)) }));
    } catch (e) { setError(e.message); }
  };

  // Start from where the items currently stand: measure them as rendered.
  useLayoutEffect(() => {
    if (positions || !stageRef.current) return;
    const stage = stageRef.current.getBoundingClientRect();
    const start = {};
    spec.items.forEach((o) => {
      const el = itemRefs.current[o.item];
      if (!el) return;
      const r = el.getBoundingClientRect();
      start[o.item] = {
        x: clamp(((r.left + r.width / 2 - stage.left) / stage.width) * 100, 0, 100),
        y: clamp(((r.bottom - stage.top) / stage.height) * 100, 0, 100),
        width: clamp((r.width / stage.width) * 100, 2, 60)
      };
    });
    setPositions(start);
  }, [positions, spec.items]);

  const toScene = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: clamp(((e.clientX - r.left) / r.width) * 100, 0, 100), y: clamp(((e.clientY - r.top) / r.height) * 100, 0, 100) };
  };
  const nameOf = Object.fromEntries(spec.items.map((o) => [o.id, o.item]));
  const byName = Object.fromEntries(spec.items.map((o) => [o.item, o]));
  const pairs = (spec.pairs || []).filter((pr) => pr.locked !== false && nameOf[pr.host] && nameOf[pr.part])
    .map((pr) => ({ ...pr, host: nameOf[pr.host], part: nameOf[pr.part] }));

  // Height of an item in % of the scene, measured as rendered (the image decides it).
  const heightOf = (item) => {
    const el = itemRefs.current[item];
    const stage = stageRef.current?.getBoundingClientRect();
    return el && stage ? (el.getBoundingClientRect().height / stage.height) * 100 : 10;
  };
  // Put a part back on its host when it has drifted off (backend enforce_pairs uses the same rule).
  const snapPart = (p, pair) => {
    const h = p[pair.host];
    const q = p[pair.part];
    if (!h || !q) return p;
    const hh = heightOf(pair.host);
    const qh = heightOf(pair.part);
    const { ok, spot } = relationOf(pair).fits({ x: h.x, y: h.y, w: h.width, h: hh }, { x: q.x, y: q.y, w: q.width, h: qh });
    return ok ? p : { ...p, [pair.part]: { ...q, x: clamp(spot.x, 0, 100), y: clamp(spot.y, 0, 99) } };
  };
  const keepPairs = (p) => pairs.reduce(snapPart, p);

  // Moving a host carries its parts by the same amount.
  const moveTo = (item, point) => setPositions((p) => {
    const before = p[item];
    const half = (before.width || 0) / 2;                // the whole sprite stays inside the scene
    const next = { ...p, [item]: { ...before, ...point, x: clamp(point.x, half, 100 - half) } };
    pairs.filter((pr) => pr.host === item && next[pr.part]).forEach((pr) => {
      next[pr.part] = { ...next[pr.part], x: clamp(next[pr.part].x + (point.x - before.x), 0, 100), y: clamp(next[pr.part].y + (point.y - before.y), 0, 100) };
    });
    return next;
  });

  const onItemDown = (e, item) => {
    e.stopPropagation();
    setSelected(item);
    dragRef.current = { item, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onItemMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    d.moved = true;
    moveTo(d.item, toScene(e));
  };
  const onItemUp = () => { dragRef.current = null; setPositions(keepPairs); };
  const onStageClick = (e) => {
    if (!selected) return;
    moveTo(selected, toScene(e));
    setPositions(keepPairs);
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(positions, art);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const styleFor = (o) => {
    const p = positions?.[o.item];
    if (!p) return o.style;
    return { left: `${clamp(p.x - p.width / 2, 0, 100 - p.width)}%`, bottom: `${100 - p.y}%`, width: `${p.width}%`, zIndex: Math.round(p.y / 10) + 1 };
  };

  return createPortal(
    <div className="sa-backdrop" role="dialog" aria-modal="true">
      <div className="sa-window">
        <header className="sa-header">
          <div>
            <h3>Arrange the scene - {spec.mission.name}</h3>
            <span>Click an item, then click where it should stand (or drag it). Its feet land where you click.</span>
          </div>
          <button type="button" className="sa-btn sa-quiet" onClick={onCancel}>Cancel</button>
          <button type="button" className="sa-btn" disabled={!positions || saving} onClick={save}>{saving ? 'Saving…' : 'Save layout'}</button>
        </header>
        {error && <div className="sa-error">{error}</div>}
        {library && (
          <div className="sa-library">
            {!rooms ? <button type="button" className="sa-btn sa-quiet" onClick={openBackgrounds}>Background…</button> : (
              <span className="sa-pictures" role="radiogroup" aria-label="Background">
                <span className="sa-muted">This room's backgrounds (free):</span>
                {rooms.variants.map((v) => (
                  <button key={v.id} type="button" role="radio" aria-checked={rooms.current === v.id} title={`Used ${v.uses} time(s)`}
                    className={`sa-picture sa-bg ${rooms.current === v.id ? 'is-selected' : ''}`} onClick={() => pickBackground(v.id)}>
                    <img src={asset(v.src)} alt="" />
                  </button>
                ))}
                {onRedoBackground && <button type="button" className="sa-btn sa-quiet" onClick={onRedoBackground}>Generate a new one (1 image)</button>}
                <button type="button" className="sa-btn sa-quiet" onClick={() => setRooms(null)}>Close</button>
              </span>
            )}
          </div>
        )}
        {(spec.checklist || []).length > 0 && (
          <details className="sa-checklist" open={(spec.checklist || []).some((c) => c.level !== 'ok')}>
            <summary>Before you publish: {(spec.checklist || []).filter((c) => c.level === 'error').length} to fix,
              {' '}{(spec.checklist || []).filter((c) => c.level === 'warn').length} to check</summary>
            <ul>
              {spec.checklist.map((c) => (
                <li key={`${c.id}-${c.text}`} className={`sa-check is-${c.level}`}>
                  <span>{LEVEL_ICON[c.level]}</span> {c.text}
                  {c.action === 'redo_background' && onRedoBackground && (
                    <button type="button" className="sa-btn sa-quiet" onClick={onRedoBackground}>Redo background</button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
        {pairs.length > 0 && (
          <div className="sa-pairs">
            <strong>Kept together so the problem shows:</strong>
            {pairs.map((pr) => <span key={`${pr.host}-${pr.part}`} className="sa-pair">{relationOf(pr).text(pr.part, pr.host)} ✓</span>)}
          </div>
        )}

        <div ref={stageRef} className={`sa-stage ${selected ? 'is-placing' : ''}`} style={{ backgroundImage: `url(${asset(spec.background_src)})` }}
          onClick={onStageClick}>
          {/* a thing made during the game (the printed label) isn't in the scene - it appears on its maker or in the bag */}
          {spec.items.filter((o) => !o.inv?.made_by).map((o) => (
            <div key={o.id} ref={(el) => { itemRefs.current[o.item] = el; }}
              className={`sa-item ${selected === o.item ? 'is-selected' : ''} ${o.kind === 'place' ? 'sa-place' : ''}`}
              style={styleFor(o)} onPointerDown={(e) => onItemDown(e, o.item)} onPointerMove={onItemMove} onPointerUp={onItemUp}
              onClick={(e) => e.stopPropagation()}>
              {o.kind === 'place' ? <span className="sa-ring" /> : pictureOf(o) ? <img src={asset(pictureOf(o))} alt={o.item} draggable={false} />
                : <span className="sa-icon" aria-hidden="true">{o.inv?.icon || '❔'}</span>}
              <span className="sa-label">{o.item}</span>
            </div>
          ))}
        </div>

        <div className="sa-controls">
          {selected && positions?.[selected] ? (
            <>
              <strong>{selected}</strong>
              <label>Size
                <input type="range" min={2} max={60} step={0.5} value={positions[selected].width}
                  onChange={(e) => setPositions((p) => ({ ...p, [selected]: { ...p[selected], width: Number(e.target.value) } }))} />
              </label>
              {byName[selected] && byName[selected].kind !== 'place' && (spec.pictures || []).length > 1 && (
                <span className="sa-pictures" role="radiogroup" aria-label="Picture">
                  <span className="sa-muted">Picture</span>
                  {spec.pictures.map((pic) => {
                    const o = byName[selected];
                    const current = pictureOf(o) === pic.src;
                    return (
                      <button key={pic.index} type="button" role="radio" aria-checked={current} title={`Picture ${pic.index + 1}`}
                        className={`sa-picture ${current ? 'is-selected' : ''}`} onClick={() => setArt((a) => ({ ...a, [o.id]: pic.index }))}>
                        <img src={asset(pic.src)} alt="" />
                      </button>
                    );
                  })}
                </span>
              )}
              {itemPics && itemPics.id === byName[selected]?.id && (itemPics.states || []).map((x) => (
                <span key={x.state} className="sa-pictures" role="radiogroup" aria-label={`When ${x.word}`}>
                  <span className="sa-muted">When {x.word}</span>
                  {x.variants.length === 0 && <span className="sa-muted">not drawn yet</span>}
                  {x.variants.map((v) => (
                    <button key={v.ref} type="button" role="radio" aria-checked={x.current === v.ref} title={`${selected} - ${x.word}`}
                      className={`sa-picture ${x.current === v.ref ? 'is-selected' : ''}`} onClick={() => pickStatePicture(x.state, v.ref)}>
                      <img src={asset(v.src)} alt="" />
                    </button>
                  ))}
                </span>
              ))}
              {itemPics && itemPics.id === byName[selected]?.id && !(itemPics.states || []).length && itemPics.variants.length > 1 && (
                <span className="sa-pictures" role="radiogroup" aria-label="Its pictures">
                  <span className="sa-muted">Its pictures</span>
                  {itemPics.variants.map((v) => (
                    <button key={v.ref} type="button" role="radio" aria-checked={itemPics.current === v.ref} title={v.looks || 'its own picture'}
                      className={`sa-picture ${itemPics.current === v.ref ? 'is-selected' : ''}`} onClick={() => pickPicture(v.ref)}>
                      <img src={asset(v.src)} alt="" />
                    </button>
                  ))}
                </span>
              )}
              {byName[selected] && byName[selected].kind !== 'place' && (
                <span className="sa-muted sa-art-note">{art[byName[selected].id] !== undefined ? ART_NOTE.creator
                  : spec.art?.[byName[selected].id]?.by ? ART_NOTE[spec.art[byName[selected].id].by]
                    : pictureOf(byName[selected]) ? '' : ART_NOTE.missing}</span>
              )}
              {byName[selected] && byName[selected].kind !== 'place' && onRedraw && (!redraw || redraw.id !== byName[selected].id) && (
                <button type="button" className="sa-btn sa-quiet" onClick={() => startRedraw(byName[selected])}>Redraw…</button>
              )}
              {redraw && byName[selected]?.id === redraw.id && (
                <span className="sa-redraw">
                  <input type="text" maxLength={160} placeholder={`How ${selected} should look (optional)`} value={redraw.looks}
                    aria-label="How it should look" onChange={(e) => setRedraw((r) => ({ ...r, looks: e.target.value }))} disabled={redraw.busy} />
                  <button type="button" className="sa-btn" disabled={redraw.busy} onClick={doRedraw}>{redraw.busy ? 'Drawing…' : 'Redraw (1 image call)'}</button>
                  {!redraw.busy && <button type="button" className="sa-btn sa-quiet" onClick={() => setRedraw(null)}>Cancel</button>}
                  {redraw.error && <span className="sa-error-inline">{redraw.error}</span>}
                </span>
              )}
              {library?.swapPicture && byName[selected]?.inv && (
                <select className="sa-swap" aria-label="Swap picture with" value="" disabled={swapping}
                  onChange={(e) => swapWith(byName[selected], e.target.value)}>
                  <option value="">{swapping ? 'Swapping…' : 'Swap picture with…'}</option>
                  {drawable.filter((o) => o.id !== byName[selected].id).map((o) => <option key={o.id} value={o.id}>{o.item}</option>)}
                </select>
              )}
              <button type="button" className="sa-btn sa-quiet" onClick={() => setSelected(null)}>Done with this item</button>
            </>
          ) : <span className="sa-muted">Pick an item to place it.</span>}
          <span className="sa-list">
            {spec.items.map((o) => (
              <span key={o.id} className="sa-chip-wrap">
                {library?.drawMany && o.inv && o.kind !== 'place' && (
                  <input type="checkbox" aria-label={`Draw a picture for ${o.item}`} checked={toDraw.includes(o.id)} disabled={drawing}
                    onChange={() => setToDraw((t) => (t.includes(o.id) ? t.filter((x) => x !== o.id) : [...t, o.id]))} />
                )}
                <button type="button" className={`sa-chip ${selected === o.item ? 'is-selected' : ''}`} onClick={() => setSelected(o.item)}>
                  {!o.src && o.inv?.icon ? `${o.inv.icon} ` : ''}{o.item}
                </button>
              </span>
            ))}
          </span>
          {library?.drawMany && drawable.length > 0 && (
            <span className="sa-draw-many">
              <button type="button" className="sa-btn sa-quiet" disabled={drawing} onClick={() => setToDraw(drawable.filter((o) => !o.src).map((o) => o.id))}>
                Tick the ones without a picture
              </button>
              <button type="button" className="sa-btn" disabled={!toDraw.length || drawing} onClick={drawPictures}>
                {drawing ? `Drawing ${toDraw.length}…` : toDraw.length > 1 ? `Draw ${toDraw.length} pictures (1 image call)` : `Draw pictures (${toDraw.length} image call${toDraw.length === 1 ? '' : 's'})`}
              </button>
              {drawError && <span className="sa-error-inline" role="alert">{drawError}</span>}
            </span>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
